import { useLocation } from 'react-router-dom';
import { useSelector } from 'react-redux';
import type { RootState } from '../store/store';
import { useStrings } from '../hooks/useStrings';
import { shouldShowIdCapture } from '../utils/idCapture';
import { shouldShowVideo } from '../utils/videoStep';

interface StepDefinition {
  routes: string[];
  label: string;
  prefillTitle: string;
  prefillDesc: string;
  icon: string;
}

export interface ProgressStep {
  label: string;
  prefillTitle: string;
  prefillDesc: string;
  icon: string;
}

export const useProgressStep = () => {
  const { pathname } = useLocation();
  const strings = useStrings();
  const { visitorTypeInfo, orgTemplateIds, flowInfoFlags, videoUrl, isPrefill, idValidationSkip, videoSkip } = useSelector(
    (state: RootState) => state.visitor
  );

  if (!visitorTypeInfo) {
    return { showProgress: false, stepNumber: 0, totalSteps: 0, stepLabel: '', prefillSteps: [] };
  }

  const S = strings.PROGRESS_STEPS;
  const isHostFieldActive = !!visitorTypeInfo?.fields?.find(
    (f) => (f.fid === 'HOST' || f.name === 'Host') && f.status === 'ACTIVE',
  );
  const steps: StepDefinition[] = [
    ...(!isPrefill && isHostFieldActive ? [{ routes: ['/select-host'], label: S.HOST.headerTitle, prefillTitle: S.HOST.prefillTitle, prefillDesc: S.HOST.prefillDesc, icon: 'bi-person' }] : []),
    { routes: ['/visitorform'], label: S.DETAILS.headerTitle, prefillTitle: S.DETAILS.prefillTitle, prefillDesc: S.DETAILS.prefillDesc, icon: 'bi-person-lines-fill' },
  ];

  if (orgTemplateIds && orgTemplateIds.length > 0) {
    steps.push({ routes: ['/signdocument'], label: S.DOCUMENT.headerTitle, prefillTitle: S.DOCUMENT.prefillTitle, prefillDesc: S.DOCUMENT.prefillDesc, icon: 'bi-file-earmark-text' });
  }
  // EN-4518: drop the ID step from the count too when the visitor's prior ID is
  // still valid, otherwise the bar would show a step they never reach.
  if (shouldShowIdCapture(flowInfoFlags?.isIdCapturingFlag, idValidationSkip)) {
    steps.push({ routes: ['/capturepicture', '/frontbackpicture'], label: S.ID_CAPTURE.headerTitle, prefillTitle: S.ID_CAPTURE.prefillTitle, prefillDesc: S.ID_CAPTURE.prefillDesc, icon: 'bi-shield-check' });
  }
  if (flowInfoFlags?.visitorPhotoFlag) {
    steps.push({ routes: ['/visitorpicture'], label: S.PHOTO.headerTitle, prefillTitle: S.PHOTO.prefillTitle, prefillDesc: S.PHOTO.prefillDesc, icon: 'bi-camera' });
  }
  // EN-4854: likewise drop the video step when the earlier watch is still valid.
  if (shouldShowVideo(videoUrl, videoSkip)) {
    steps.push({ routes: ['/visitor-video'], label: S.VIDEO.headerTitle, prefillTitle: S.VIDEO.prefillTitle, prefillDesc: S.VIDEO.prefillDesc, icon: 'bi-camera-video' });
  }

  const currentIndex = steps.findIndex(step =>
    step.routes.some(r => pathname === r)
  );

  const prefillSteps: ProgressStep[] = steps.map(s => ({ label: s.label, prefillTitle: s.prefillTitle, prefillDesc: s.prefillDesc, icon: s.icon }));

  if (currentIndex === -1) {
    return { showProgress: false, stepNumber: 0, totalSteps: steps.length, stepLabel: '', prefillSteps };
  }

  return {
    showProgress: true,
    stepNumber: currentIndex + 1,
    totalSteps: steps.length,
    stepLabel: steps[currentIndex].label,
    prefillSteps,
  };
};
